/*!
 * Copyright Optimus Ride 2019
 */

import { RemoteMonitorCommandServiceClient } from '../../grpc/Remote-monitor-commandServiceClientPb';
import {
    ListWaypointsRequest,
    GetRouteProposalRequest,
    ConfirmRouteProposalRequest,
    SetAutonomyReadyRequest,
    ConfirmOvcEventRequest,
    SendPulloverRequest
} from '../../grpc/remote-monitor-command_pb';

import { WaypointId } from '../../grpc/common_pb';

import { GRPC_REMOTE_MONITOR_PORT } from '../../constants';
import Auth from '../../auth/abstract-auth';
import { requestMetadata } from '../util';
import { RouteNetworkInfo, toRouteNetworkInfo } from '../../@types/grpc/remote-monitor-command';
import { toRouteProposal, RouteProposal, CommandHeader, fromCommandHeader } from '../../@types/grpc/route-management';
import { TripStatus, fromTripStatus } from '../../@types/grpc/waypoint-list';

export default class RemoteMonitorServiceConsumer {
    private remoteMonitorServiceClient: RemoteMonitorCommandServiceClient;
    private auth: Auth;
    private oriApiKey: undefined | string;

    constructor(auth: Auth, hostname: string, oriApiKey?: undefined | string) {
        this.auth = auth;
        this.oriApiKey = oriApiKey;
        this.remoteMonitorServiceClient = new RemoteMonitorCommandServiceClient(
            `${hostname}:${GRPC_REMOTE_MONITOR_PORT}`, null, null,
        )
    }

    public listWaypoints = (vehicleId: string): Promise<RouteNetworkInfo> => {
        const request = new ListWaypointsRequest();
        request.setVehicleId(vehicleId);
        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.listWaypoints(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
                if (!err) {
                    call.on('status', (status) => {
                        if (status.code !== 0) {
                            reject(status); // TODO(peter-or) Error handling
                        } else {
                            resolve(toRouteNetworkInfo(response));
                        }
                    });
                } else {
                    reject(err);
                }
            });
        });
    }

    public requestRouteProposal = (vehicleId: string, waypointQueue: Array<string>, tripStatus: TripStatus, operatingAreaId: number): Promise<RouteProposal> => {
        const waypoints = waypointQueue.map(waypointId => {
            const waypointObj = new WaypointId();
            waypointObj.setId(waypointId);
            return waypointObj;
        });

        const request = new GetRouteProposalRequest();
        request.setVehicleId(vehicleId);
        request.setWaypointIdsList(waypoints);
        request.setOperatingAreaId(operatingAreaId);
        request.setTerminus(fromTripStatus(tripStatus));

        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.getRouteProposal(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
                if (!err) {
                    call.on('status', (status) => {
                        if (status.code !== 0) {
                            reject(status); // TODO(peter-or) Error handling
                        } else {
                            resolve(toRouteProposal(response));
                        }
                    });
                } else {
                    reject(err);
                }
            });
        });
    }

    public acceptRouteProposal = (vehicleId: string, commandId: CommandHeader): Promise<void> => {
        const request = new ConfirmRouteProposalRequest();
        request.setVehicleId(vehicleId);
        request.setHeader(fromCommandHeader(commandId));

        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.confirmRouteProposal(
                request,
                await requestMetadata(this.auth, this.oriApiKey),
                (err, _response) => {
                    if (!err) {
                        call.on('status', (status) => {
                            if (status.code !== 0) {
                                reject(status); // TODO(peter-or) Error handling
                            } else {
                                resolve();
                            }
                        });
                    } else {
                        reject(err);
                    }
                });
        });
    }

    public setAutonomyReady = (vehicleId: string): Promise<void> => {
        const request = new SetAutonomyReadyRequest();
        request.setVehicleId(vehicleId);

        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.setAutonomyReady(
                request,
                await requestMetadata(this.auth, this.oriApiKey),
                (err, _response) => {
                    if (!err) {
                        call.on('status', (status) => {
                            if (status.code !== 0) {
                                reject(status); // TODO(peter-or) Error handling
                            } else {
                                resolve();
                            }
                        });
                    } else {
                        reject(err);
                    }
                });
        });
    }

    public confirmOvcEvent = (vehicleId: string, commandId: CommandHeader, confirmOvc: boolean): Promise<void> => {
        const request = new ConfirmOvcEventRequest();
        request.setVehicleId(vehicleId);
        request.setHeader(fromCommandHeader(commandId));
        request.setConfirmOvc(confirmOvc);

        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.confirmOvcEvent(
                request,
                await requestMetadata(this.auth, this.oriApiKey),
                (err, _response) => {
                    if (!err) {
                        call.on('status', (status) => {
                            if (status.code !== 0) {
                                reject(status); // TODO(peter-or) Error handling
                            } else {
                                resolve();
                            }
                        });
                    } else {
                        reject(err);
                    }
                });
        });
    }

    public sendPullover = (vehicleId: string, confirmPullover: boolean): Promise<void> => {
        const request = new SendPulloverRequest();
        request.setVehicleId(vehicleId);
        request.setConfirmPullover(confirmPullover);

        return new Promise(async (resolve, reject) => {
            const call = this.remoteMonitorServiceClient.sendPullover(
                request,
                await requestMetadata(this.auth, this.oriApiKey),
                (err, _response) => {
                    if (!err) {
                        call.on('status', (status) => {
                            if (status.code !== 0) {
                                reject(status); // TODO(peter-or) Error handling
                            } else {
                                resolve();
                            }
                        });
                    } else {
                        reject(err);
                    }
                });
        });
    }
}
